import { useCallback, useEffect, useState } from 'react';
import { AdminCardGridShimmer } from '../../common/Shimmer.jsx';
import { useDestructiveAction } from '../../../contexts/DestructiveActionContext.jsx';
import { useAdminDialog } from '../../../contexts/AdminDialogContext.jsx';
import { hpApi } from './hpApi.js';
import {
  HpIcon, HpSelect, HpSwitch, HpField, HpCounter, HpImageField, HpBadge, hpImageSrc, useHpToast,
} from './HpUi.jsx';

const EXPERT_TYPES = [
  { value: 'supervisor', label: '監修者' },
  { value: 'advisor', label: '顧問' },
];

/** 種類ごとの表示・入力定義 */
function toPersonPayload(person) {
  const payload = { ...person };
  delete payload.id;
  delete payload.createdAt;
  delete payload.updatedAt;
  return payload;
}

const PEOPLE_CONFIG = {
  team: {
    title: 'チーム',
    unit: 'メンバー',
    imageKey: 'photo',
    altKey: 'photoAlt',
    nameKey: 'name',
    aspect: '4 / 5',
    empty: { name: '', role: '', profile: '', photo: '', photoAlt: '', published: false, comingSoon: false },
    subtitle: (x) => x.role,
    hint: '会社概要ページとトップページの「経営・開発メンバー」に表示されます。公開には写真が必要です（「準備中」にしたメンバーは写真と文字を伏せて枠だけ表示するため、写真未登録でも掲載できます）',
    comingSoonDesc: 'オンの間は、写真・役職・氏名・プロフィールを HP に出さず、「Coming soon」と伏せ字の枠だけを表示します',
  },
  experts: {
    title: '専門家',
    unit: '専門家',
    imageKey: 'photo',
    altKey: 'photoAlt',
    nameKey: 'name',
    aspect: '4 / 5',
    empty: { type: 'supervisor', name: '', affiliations: [], specialty: '', profile: '', relatedProducts: [], photo: '', photoAlt: '', published: false, comingSoon: false },
    subtitle: (x) => `${x.type === 'advisor' ? '顧問' : '監修者'}${x.specialty ? `／${x.specialty}` : ''}`,
    hint: '専門家ページとトップページに表示されます。公開には写真が必要です（「準備中」にした人物は写真と文字を伏せて枠だけ表示するため、写真未登録でも掲載できます）',
    comingSoonDesc: 'オンの間は、写真・氏名・所属・専門領域・プロフィールを HP に出さず、「Coming soon」と伏せ字の枠だけを表示します（区分の「監修者／顧問」は表示されます）',
  },
  partners: {
    title: '提携企業',
    unit: '提携企業',
    imageKey: 'logo',
    altKey: 'logoAlt',
    nameKey: 'companyName',
    aspect: '16 / 9',
    empty: { companyName: '', relationType: '', description: '', logo: '', logoAlt: '', url: '', published: false, comingSoon: false },
    subtitle: (x) => x.relationType,
    hint: '専門家ページとトップページの「パートナー」に表示されます。公開にはロゴが必要です（「準備中」の間はロゴ未登録でも掲載できます）',
    comingSoonDesc: 'オンの間は、ロゴ・企業名・提携区分・紹介文・Webサイトへのリンクを HP に出さず、「Coming soon」と伏せ字の枠だけを表示します',
  },
};

function PersonForm({ kind, value, onChange, siteUrl, canWrite }) {
  const cfg = PEOPLE_CONFIG[kind];
  const set = (patch) => onChange({ ...value, ...patch });
  const listText = (arr) => (arr || []).join('\n');
  const fromList = (t) => t.split('\n').map((s) => s.trim()).filter(Boolean);

  return (
    <div className="hp-person-form">
      <div className="hp-person-form__image">
        <HpField label={kind === 'partners' ? 'ロゴ' : '写真'}>
          <HpImageField value={value[cfg.imageKey]} onChange={(url) => set({ [cfg.imageKey]: url })} folder={kind} siteUrl={siteUrl} disabled={!canWrite} aspect={cfg.aspect} />
        </HpField>
        <HpField label="代替テキスト" hint="画像を読み上げる際の説明。未入力なら名前を使います">
          <input className="hp-input" value={value[cfg.altKey]} disabled={!canWrite} onChange={(e) => set({ [cfg.altKey]: e.target.value })} />
        </HpField>
      </div>
      <div className="hp-person-form__fields">
        {kind === 'partners' ? (
          <>
            <HpField label="企業名" required>
              <input className="hp-input" value={value.companyName} disabled={!canWrite} onChange={(e) => set({ companyName: e.target.value })} />
            </HpField>
            <HpField label="提携区分" hint="例：事業・システム連携パートナー">
              <input className="hp-input" value={value.relationType} disabled={!canWrite} onChange={(e) => set({ relationType: e.target.value })} />
            </HpField>
            <HpField label="紹介文" counter={<HpCounter value={value.description} max={1000} />}>
              <textarea className="hp-textarea" rows={4} value={value.description} disabled={!canWrite} onChange={(e) => set({ description: e.target.value })} />
            </HpField>
            <HpField label="Webサイト" hint="https:// から始まる URL（任意）">
              <input className="hp-input" value={value.url} disabled={!canWrite} onChange={(e) => set({ url: e.target.value })} placeholder="https://" />
            </HpField>
          </>
        ) : (
          <>
            {kind === 'experts' && (
              <HpField label="区分">
                <HpSelect value={value.type} options={EXPERT_TYPES} onChange={(v) => set({ type: v })} ariaLabel="区分" disabled={!canWrite} />
              </HpField>
            )}
            <HpField label="氏名" required>
              <input className="hp-input" value={value.name} disabled={!canWrite} onChange={(e) => set({ name: e.target.value })} />
            </HpField>
            {kind === 'team' && (
              <HpField label="役職">
                <input className="hp-input" value={value.role} disabled={!canWrite} onChange={(e) => set({ role: e.target.value })} />
              </HpField>
            )}
            {kind === 'experts' && (
              <>
                <HpField label="所属" hint="1行に1つ">
                  <textarea className="hp-textarea" rows={3} value={listText(value.affiliations)} disabled={!canWrite} onChange={(e) => set({ affiliations: fromList(e.target.value) })} />
                </HpField>
                <HpField label="専門領域">
                  <input className="hp-input" value={value.specialty} disabled={!canWrite} onChange={(e) => set({ specialty: e.target.value })} />
                </HpField>
                <HpField label="関連製品" hint="1行に1つ（例：Synapse Perio）">
                  <textarea className="hp-textarea" rows={2} value={listText(value.relatedProducts)} disabled={!canWrite} onChange={(e) => set({ relatedProducts: fromList(e.target.value) })} />
                </HpField>
              </>
            )}
            <HpField label="プロフィール" counter={<HpCounter value={value.profile} max={3000} />}>
              <textarea className="hp-textarea" rows={6} value={value.profile} disabled={!canWrite} onChange={(e) => set({ profile: e.target.value })} />
            </HpField>
          </>
        )}
        <HpSwitch
          checked={value.published}
          disabled={!canWrite}
          onChange={(v) => set({ published: v })}
          label="HP に掲載する"
          description="掲載の許諾が取れていることを確認してからオンにしてください"
        />
        <HpSwitch
          checked={value.comingSoon === true}
          disabled={!canWrite}
          onChange={(v) => set({ comingSoon: v })}
          label="「準備中（Coming Soon）」として表示する"
          description={`${cfg.comingSoonDesc}。「HP に掲載する」がオンのときだけ HP に出ます`}
        />
      </div>
    </div>
  );
}

function PersonModal({ kind, initial, siteUrl, canWrite, onClose, onSaved }) {
  const cfg = PEOPLE_CONFIG[kind];
  const toast = useHpToast();
  const { showChoice } = useAdminDialog();
  const [value, setValue] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [dirty, setDirty] = useState(false);
  const isNew = !initial.id;

  const close = async () => {
    if (saving) return;
    if (dirty) {
      const choice = await showChoice({
        title: '変更を保存しますか',
        message: '保存していない変更があります。',
        choices: [
          { value: 'discard', label: '破棄して閉じる', danger: true },
          ...(canWrite ? [{ value: 'save', label: '保存して閉じる', primary: true }] : []),
        ],
      });
      if (choice === 'save') return save();
      if (choice !== 'discard') return undefined;
    }
    onClose();
    return undefined;
  };

  const save = async () => {
    if (!String(value[cfg.nameKey] || '').trim()) {
      toast.error(kind === 'partners' ? '企業名を入力してください' : '氏名を入力してください');
      return;
    }
    if (value.published && !value.comingSoon && !value[cfg.imageKey]) {
      toast.error(kind === 'partners'
        ? '公開する提携企業にはロゴが必要です（準備中表示なら未登録でも掲載できます）'
        : `公開する${cfg.unit}には写真が必要です（準備中表示なら未登録でも掲載できます）`);
      return;
    }
    setSaving(true);
    try {
      const payload = toPersonPayload(value);
      if (isNew) await hpApi.createPerson(kind, payload);
      else await hpApi.updatePerson(kind, value.id, payload);
      toast.success(`${cfg.unit}を保存しました`);
      onSaved();
    } catch (err) {
      toast.error(err.message || '保存に失敗しました');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" onMouseDown={(e) => { if (e.target === e.currentTarget) close(); }}>
      <div className="modal modal-wide hp-modal" role="dialog" aria-modal="true" aria-labelledby="hp-person-title">
        <div className="modal-header">
          <h2 id="hp-person-title">{isNew ? `${cfg.unit}を追加` : `${cfg.unit}を編集`}</h2>
          <button type="button" className="modal-close" onClick={close} aria-label="閉じる">&times;</button>
        </div>
        <div className="modal-body">
          <PersonForm kind={kind} value={value} siteUrl={siteUrl} canWrite={canWrite} onChange={(v) => { setValue(v); setDirty(true); }} />
        </div>
        <div className="modal-footer">
          <button type="button" className="btn btn-secondary" onClick={close}>キャンセル</button>
          {canWrite && <button type="button" className="btn btn-primary" onClick={save} disabled={saving}>{saving ? '保存中…' : '保存'}</button>}
        </div>
      </div>
    </div>
  );
}

export default function HpPeoplePanel({ kind, siteUrl, canWrite }) {
  const cfg = PEOPLE_CONFIG[kind];
  const toast = useHpToast();
  const { requestDestructiveAction } = useDestructiveAction();
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null);
  const [ordering, setOrdering] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await hpApi.listPeople(kind);
      setItems(res.items || []);
    } catch (err) {
      toast.error(err.message || `${cfg.title}の取得に失敗しました`);
      setItems([]);
    }
  }, [kind, cfg.title, toast]);

  useEffect(() => { setItems(null); load(); }, [load]);

  const move = async (i, d) => {
    const j = i + d;
    if (!items || j < 0 || j >= items.length) return;
    const next = [...items];
    [next[i], next[j]] = [next[j], next[i]];
    setItems(next);
    setOrdering(true);
    try {
      await hpApi.reorderPeople(kind, next.map((x) => x.id));
    } catch (err) {
      toast.error(err.message || '並び順の保存に失敗しました');
      await load();
    } finally {
      setOrdering(false);
    }
  };

  const togglePublished = async (item) => {
    try {
      await hpApi.updatePerson(kind, item.id, { ...toPersonPayload(item), published: !item.published });
      toast.success(item.published ? '非掲載にしました' : '掲載しました');
      await load();
    } catch (err) {
      toast.error(err.message || '変更に失敗しました');
    }
  };

  const toggleComingSoon = async (item) => {
    const next = !item.comingSoon;
    try {
      await hpApi.updatePerson(kind, item.id, { ...toPersonPayload(item), comingSoon: next });
      toast.success(next ? '「準備中」表示にしました' : '「準備中」表示を解除しました');
      await load();
    } catch (err) {
      toast.error(err.message || '変更に失敗しました');
    }
  };

  const remove = async (item) => {
    const result = await requestDestructiveAction({
      title: `${cfg.unit}を削除`,
      message: `「${item[cfg.nameKey]}」を削除します。HP からも表示されなくなり、元に戻せません。一時的に隠す場合は「非掲載」を使ってください。`,
      confirmLabel: '削除する',
    });
    if (!result) return;
    try {
      await hpApi.deletePerson(kind, item.id, result.reason);
      toast.success('削除しました');
      await load();
    } catch (err) {
      toast.error(err.message || '削除に失敗しました');
    }
  };

  return (
    <div className="hp-panel">
      <div className="hp-toolbar">
        <p className="hp-toolbar__hint">{cfg.hint}</p>
        {canWrite && (
          <button type="button" className="hp-btn hp-btn--primary" onClick={() => setEditing({ ...cfg.empty })}><HpIcon.Plus />{cfg.unit}を追加</button>
        )}
      </div>

      {items === null ? (
        <AdminCardGridShimmer cards={3} withHeader={false} />
      ) : items.length === 0 ? (
        <div className="hp-empty">まだ登録がありません。</div>
      ) : (
        <ul className="hp-people">
          {items.map((item, i) => (
            <li key={item.id} className={`hp-person${item.published ? '' : ' is-hidden'}`}>
              <div className={`hp-person__img${kind === 'partners' ? ' is-logo' : ''}`}>
                {item[cfg.imageKey]
                  ? <img src={hpImageSrc(item[cfg.imageKey], siteUrl)} alt="" />
                  : <span className="hp-person__noimg"><HpIcon.Image size={20} /></span>}
              </div>
              <div className="hp-person__body">
                <div className="hp-list__badges">
                  <HpBadge tone={item.published ? 'success' : 'muted'}>{item.published ? '掲載中' : '非掲載'}</HpBadge>
                  {item.comingSoon && <HpBadge tone="warning">準備中</HpBadge>}
                </div>
                <span className="hp-person__name">{item[cfg.nameKey]}</span>
                <span className="hp-person__sub">{cfg.subtitle(item)}</span>
              </div>
              <div className="hp-person__tools">
                {canWrite && (
                  <>
                    <button type="button" className="hp-icon-btn" onClick={() => move(i, -1)} disabled={i === 0 || ordering} aria-label="前へ"><HpIcon.Up /></button>
                    <button type="button" className="hp-icon-btn" onClick={() => move(i, 1)} disabled={i === items.length - 1 || ordering} aria-label="後ろへ"><HpIcon.Down /></button>
                  </>
                )}
                <button type="button" className="hp-btn hp-btn--ghost hp-btn--sm" onClick={() => setEditing(item)}><HpIcon.Edit size={12} />{canWrite ? '編集' : '詳細'}</button>
                {canWrite && (
                  <>
                    <button type="button" className="hp-btn hp-btn--text hp-btn--sm" onClick={() => toggleComingSoon(item)}>{item.comingSoon ? '準備中を解除' : '準備中にする'}</button>
                    <button type="button" className="hp-btn hp-btn--text hp-btn--sm" onClick={() => togglePublished(item)}>{item.published ? '非掲載にする' : '掲載する'}</button>
                    <button type="button" className="hp-icon-btn hp-icon-btn--danger" onClick={() => remove(item)} aria-label="削除"><HpIcon.Trash /></button>
                  </>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <PersonModal
          kind={kind}
          initial={editing}
          siteUrl={siteUrl}
          canWrite={canWrite}
          onClose={() => setEditing(null)}
          onSaved={async () => { setEditing(null); await load(); }}
        />
      )}
    </div>
  );
}
